import { useState } from 'react'
import {
  useCreatePlatform,
  useDeletePlatform,
  usePatchPlatform,
  usePlatforms,
} from '../api/hooks'
import type { Platform } from '../api/types'
import { Modal } from '../components/Modal'

// Registry ids only (formats/__init__.py's authoritative NAMES) — no
// aliases. "loco_json" etc. exist so pull's `?format=` override and the
// importer's content-sniffer can accept legacy spellings for a one-off
// request; a platform's own stored default_format must be exactly one
// unambiguous id, so only these four are ever offered here. Mirrors the
// same option set as the export dialog (editor/Dialogs.tsx).
const DEFAULT_FORMATS: { value: string; label: string }[] = [
  { value: 'json', label: 'Web JSON' },
  { value: 'strings', label: 'iOS .strings' },
  { value: 'xcstrings', label: 'iOS .xcstrings' },
  { value: 'android_xml', label: 'Android strings.xml' },
]

export function PlatformsTab() {
  const platforms = usePlatforms()
  const [showAdd, setShowAdd] = useState(false)
  const [renaming, setRenaming] = useState<Platform | null>(null)

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-ink-600">
          A new platform is created empty — zero keys — until a dev pushes to it. It is born
          with a default project and its source locale already assigned, so the grid and pull
          both work immediately; pushing more keys and assigning more locales are separate,
          explicit steps.
        </p>
        <button className="btn btn-primary shrink-0" onClick={() => setShowAdd(true)}>
          + Add a platform
        </button>
      </div>

      {platforms.isError && (
        <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900">
          Could not load platforms: {(platforms.error as Error).message}
        </p>
      )}

      <div className="overflow-hidden rounded-lg border border-ink-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-ink-100 text-2xs uppercase tracking-wider text-ink-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">Slug</th>
              <th className="px-3 py-2 text-left font-semibold">Name</th>
              <th className="px-3 py-2 text-left font-semibold">Default format</th>
              <th className="px-3 py-2 text-left font-semibold">Keys</th>
              <th className="px-3 py-2 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">
            {(platforms.data ?? []).map((platform) => (
              <PlatformRow
                key={platform.id}
                platform={platform}
                onRename={() => setRenaming(platform)}
              />
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <AddPlatformDialog onClose={() => setShowAdd(false)} />}
      {renaming && <RenamePlatformDialog platform={renaming} onClose={() => setRenaming(null)} />}
    </div>
  )
}

function PlatformRow({
  platform,
  onRename,
}: {
  platform: Platform
  onRename: () => void
}) {
  const del = useDeletePlatform()

  const handleDelete = () => {
    const warning =
      platform.total_keys > 0
        ? `Delete ${platform.slug}? It has ${platform.total_keys} active key(s) — they, their ` +
          'translations and revisions are kept (soft delete), but the platform disappears from ' +
          'every list, the grid, pull and status until an admin restores it.'
        : `Delete ${platform.slug}? It can be restored later; its data (if any) is kept.`
    if (!window.confirm(warning)) return
    del.mutate({ slug: platform.slug, confirmActiveKeys: platform.total_keys > 0 })
  }

  return (
    <tr>
      <td className="px-3 py-2 font-mono text-ink-800">{platform.slug}</td>
      <td className="px-3 py-2">{platform.name}</td>
      <td className="px-3 py-2">
        <span className="chip border-ink-300 bg-white text-ink-700">{platform.default_format}</span>
      </td>
      <td className="px-3 py-2">
        {platform.total_keys > 0 ? (
          platform.total_keys
        ) : (
          <span
            className="text-amber-700"
            title="No dev has pushed any keys yet — see POST /api/v1/push with a write-scoped API key."
          >
            0 — empty, awaiting a push
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <div className="flex gap-1.5">
          <button className="btn" onClick={onRename}>
            Edit
          </button>
          <button className="btn" disabled={del.isPending} onClick={handleDelete}>
            Delete
          </button>
        </div>
      </td>
    </tr>
  )
}

function AddPlatformDialog({ onClose }: { onClose: () => void }) {
  const create = useCreatePlatform()
  const [slug, setSlug] = useState('')
  const [name, setName] = useState('')
  const [defaultFormat, setDefaultFormat] = useState(DEFAULT_FORMATS[0].value)

  const submit = () => {
    create.mutate(
      { slug: slug.trim(), name: name.trim(), default_format: defaultFormat },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      title="Add a platform"
      description="Born with a default project and its source locale assigned, so it works immediately — but with zero keys until a dev runs POST /api/v1/push with a write-scoped API key."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={!slug.trim() || create.isPending}
            onClick={submit}
          >
            {create.isPending ? 'Creating…' : 'Create platform'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="new-platform-slug">
            Slug
          </label>
          <input
            id="new-platform-slug"
            className="field mt-1 font-mono"
            placeholder="tv, desktop, watch…"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
          />
          <p className="mt-1 text-2xs text-ink-600">
            Lowercase, url-safe (a-z, 0-9, '-'). Appears in URLs like /api/platforms/{'{slug}'}/grid
            and every dev's push/pull call — it cannot be renamed later, so choose carefully.
          </p>
        </div>
        <div>
          <label className="label" htmlFor="new-platform-name">
            Display name
          </label>
          <input
            id="new-platform-name"
            className="field mt-1"
            placeholder="TV"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="new-platform-format">
            Default format
          </label>
          <select
            id="new-platform-format"
            className="field mt-1"
            value={defaultFormat}
            onChange={(event) => setDefaultFormat(event.target.value)}
          >
            {DEFAULT_FORMATS.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-2xs text-ink-600">
            What a client's pull gets when it omits `?format=`. Changeable later.
          </p>
        </div>
        {create.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(create.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}

function RenamePlatformDialog({
  platform,
  onClose,
}: {
  platform: Platform
  onClose: () => void
}) {
  const patch = usePatchPlatform()
  const [name, setName] = useState(platform.name)
  const [defaultFormat, setDefaultFormat] = useState(platform.default_format)
  const formatChanged = defaultFormat !== platform.default_format

  const submit = () => {
    patch.mutate(
      { slug: platform.slug, name: name.trim(), default_format: defaultFormat },
      { onSuccess: onClose },
    )
  }

  return (
    <Modal
      title={`Edit ${platform.slug}`}
      description="The slug itself cannot be renamed here: it is what every dev's push/pull/status call and every mobile build config is pinned to, with no forwarding for an old spelling."
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={!name.trim() || patch.isPending} onClick={submit}>
            {patch.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <label className="label" htmlFor="edit-platform-name">
            Display name
          </label>
          <input
            id="edit-platform-name"
            className="field mt-1"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="edit-platform-format">
            Default format
          </label>
          <select
            id="edit-platform-format"
            className="field mt-1"
            value={defaultFormat}
            onChange={(event) => setDefaultFormat(event.target.value)}
          >
            {DEFAULT_FORMATS.map((format) => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
          {formatChanged && (
            <p className="mt-1 text-2xs text-amber-700">
              Changes what every client's pull gets when it omits `?format=` — this bumps
              keys_version, so cached pulls are invalidated.
            </p>
          )}
        </div>
        {patch.isError && (
          <p role="alert" className="rounded border border-rose-300 bg-rose-50 px-2 py-1.5 text-xs text-rose-900">
            {(patch.error as Error).message}
          </p>
        )}
      </div>
    </Modal>
  )
}
